// The month — the planning altitude, in Zanetto Tools' shape (round 22).
//
// Weeks as rows, cells 96px minimum, and per-JOB colour-filled chips (max
// three, then "+N more") in place of the old per-booking tinted ones. Today's
// date number is a filled circle — `bg-brand-500 text-ink`, because orange
// carries ink, never white.
//
// The "+N more" stays a BUTTON opening the day panel — a Dayboard extra; ZT
// renders it as dead text. Same for the hover-revealed "+" add affordance.
import { useMemo } from 'react';
import {
  formatClockRange,
  todayISO,
  sameMonth,
  WEEKDAY_LABELS,
  type DateOnly,
} from '../../lib/dates';
import { jobColour } from '../../lib/jobColour';
import { groupDayByJob, type JobDayGroup } from '../../lib/jobGroups';
import { nwShade, type NwLookup, type WorksOn } from '../../lib/nonWorkingDays';
import type { CalendarBooking, Unavailability } from '../../schedulingApi';

const CHIP_LIMIT = 3;

function chunkWeeks(days: DateOnly[]): DateOnly[][] {
  const weeks: DateOnly[][] = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return weeks;
}

export function MonthView({
  days,
  anchorMonth,
  byDay,
  awayByDay,
  onOpenDay,
  onAddOn,
  onSelectGroup,
  nwInfo,
  worksOn,
}: {
  days: DateOnly[];
  anchorMonth: DateOnly;
  byDay: Map<string, CalendarBooking[]>;
  awayByDay: Map<string, Unavailability[]>;
  onOpenDay: (date: DateOnly) => void;
  onAddOn: (date: DateOnly) => void;
  onSelectGroup: (group: JobDayGroup, date: DateOnly) => void;
  nwInfo: NwLookup;
  worksOn: WorksOn;
}) {
  const today = todayISO();
  const weeks = useMemo(() => chunkWeeks(days), [days]);

  const groupsByDay = useMemo(() => {
    const map = new Map<DateOnly, JobDayGroup[]>();
    for (const date of days) {
      const dayBookings = (byDay.get(date) ?? []).filter((b) => worksOn(b, date));
      map.set(date, groupDayByJob(dayBookings));
    }
    return map;
  }, [days, byDay, worksOn]);

  return (
    <div className="card overflow-x-auto">
      <div className="min-w-[720px]">
        <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
          {WEEKDAY_LABELS.map((label) => (
            <div
              key={label}
              className="border-r border-gray-200 py-2 text-center text-xs font-semibold uppercase tracking-wide text-gray-500 last:border-r-0"
            >
              {label}
            </div>
          ))}
        </div>

        {weeks.map((week) => (
          <div
            key={week[0]}
            className="grid grid-cols-7 border-b border-gray-100 last:border-b-0"
          >
            {week.map((date) => {
              const nw = nwInfo(date);
              const isToday = date === today;
              const inMonth = sameMonth(date, anchorMonth);
              const groups = groupsByDay.get(date) ?? [];
              const away = awayByDay.get(date) ?? [];
              const overflow = groups.length - CHIP_LIMIT;

              const cellBg = isToday
                ? 'bg-brand-50/40'
                : nw
                  ? nwShade(nw)
                  : !inMonth
                    ? 'bg-gray-50/70'
                    : '';

              return (
                <div
                  key={date}
                  className={`group min-h-24 border-r border-gray-100 p-1 last:border-r-0 ${cellBg}`}
                >
                  <div className="mb-1 flex items-start justify-between gap-1">
                    {nw?.dated ? (
                      <span
                        className="truncate text-[10px] font-medium leading-tight text-red-700"
                        title={nw.name ?? undefined}
                      >
                        {nw.name}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onAddOn(date)}
                        aria-label={`Book something on ${date}`}
                        title="Book something on this day"
                        className="rounded px-1 text-sm leading-none text-gray-500 opacity-0 transition-opacity focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-brand-600 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100"
                      >
                        +
                      </button>
                    )}
                    {isToday ? (
                      <button
                        type="button"
                        onClick={() => onOpenDay(date)}
                        aria-label={`Everything on ${date}`}
                        className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-brand-500 text-xs font-bold text-ink focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1"
                      >
                        {Number(date.slice(8, 10))}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onOpenDay(date)}
                        aria-label={`Everything on ${date}`}
                        className={`flex-none rounded px-1 text-xs font-medium tabular-nums focus:outline-none focus:ring-2 focus:ring-brand-600 ${
                          inMonth ? 'text-gray-700' : 'text-gray-300'
                        }`}
                      >
                        {Number(date.slice(8, 10))}
                      </button>
                    )}
                  </div>

                  {away.length > 0 && (
                    <div
                      className="schedule-away-strip mb-0.5 truncate"
                      title={away.map((a) => a.reason).join(', ')}
                    >
                      {away.length} away
                    </div>
                  )}

                  <div className="space-y-0.5">
                    {groups.slice(0, CHIP_LIMIT).map((group) => {
                      const colour = jobColour(group.jobNumber, group.jobId, group.inConflict);
                      return (
                        <button
                          key={group.key}
                          type="button"
                          onClick={() => onSelectGroup(group, date)}
                          className={`w-full rounded px-1.5 py-0.5 text-left text-xs transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-brand-600 ${colour.bar} ${colour.barText}`}
                        >
                          <div className="truncate font-semibold leading-tight">{group.title}</div>
                          <div className="flex items-baseline justify-between gap-3 leading-tight">
                            <span className="opacity-90">{group.subtitle}</span>
                            <span className="whitespace-nowrap tabular-nums opacity-90">
                              {formatClockRange(group.startTime, group.endTime) ?? 'All day'}
                            </span>
                          </div>
                          {/* Wraps rather than truncates — ZT's call: a month
                              cell's job is "who is on it", and a clipped list
                              answers a different question. */}
                          <div className="text-[10px] leading-snug opacity-75">
                            {group.resources.join(', ')}
                          </div>
                        </button>
                      );
                    })}
                    {overflow > 0 && (
                      <button
                        type="button"
                        onClick={() => onOpenDay(date)}
                        className="rounded px-1 text-left text-xs font-medium text-brand-800 hover:bg-brand-50 focus:outline-none focus:ring-2 focus:ring-brand-600"
                      >
                        +{overflow} more
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
